import { useState } from 'react'
import ResetBookingModal from './ResetBookingModal'
import styles from './index.module.scss'
import { cleanBookingInfo } from 'state/booking/actions'
import { useDispatch } from 'react-redux'
import { useRouter } from 'next/router'
import CLButton from 'component_library/1_Atoms/Button'
import classNames from 'classnames'
import {
  removeKeywordFromStoredSearchQuery,
  removeVerifiedInsuranceInfo,
} from 'utils/common'
import { getSfAddress, getProviderSearchUrl } from 'hooks/salesforce/utils'
import useSalesforceLead from 'hooks/salesforce/useSalesforceLead'
import useSalesforceCase from 'hooks/salesforce/useSalesforceCase'
import useFetchLocation from 'hooks/useFetchLocation'
import useGetTreatment from 'hooks/treatments/useGetTreatment'
import { TreatmentFlags, treatmentFlagValues } from 'constants/treatments'
import { usedForFeature } from 'constants/logEvents'
import useThriveAuth from 'hooks/useThriveAuth'
import { resetAcceptedMedicaidSession } from 'app/Bookings/utils'

type ResetSupportBookingProps = {
  displayText?: string
  variant?: 'button' | 'link'
  ctaClassOverride?: string
}

const ResetSupportBooking: React.FC<ResetSupportBookingProps> = ({
  displayText = 'Start new search',
  variant = 'button',
  ctaClassOverride = '',
}) => {
  const router = useRouter()
  const dispatch = useDispatch()
  const [showModal, setShowModal] = useState<boolean>(false)
  const { isThirdPartyAgent } = useThriveAuth()

  const { lead, hasLeadId } = useSalesforceLead()
  const { sfCase, sfAccount, hasCaseId } = useSalesforceCase()
  const isSalesforce = !!lead || !!sfCase
  const sfLocation =
    sfAccount?.PersonMailingAddress?.city ??
    sfAccount?.PersonMailingAddress?.state ??
    sfAccount?.PersonMailingAddress?.postalCode

  const { locationData } = useFetchLocation({
    locationString: sfLocation,
    fetchLocationEnabled: !!sfLocation,
    usedForFeature: usedForFeature.ResetSupportBooking,
  })

  const sfAccountAddress = getSfAddress(sfAccount, locationData)

  const sfCaseAutoFill =
    useGetTreatment(TreatmentFlags.SALESFORCE_CASE_AUTOFILL) ===
    treatmentFlagValues.ON

  const providerSearchUrl = getProviderSearchUrl(
    hasLeadId,
    lead,
    hasCaseId,
    sfCase,
    sfAccountAddress,
    sfCaseAutoFill
  )

  const resetBooking = async () => {
    setShowModal(false)

    if (!isThirdPartyAgent) {
      removeKeywordFromStoredSearchQuery()
    }

    removeVerifiedInsuranceInfo()
    await router.replace(providerSearchUrl)

    dispatch(cleanBookingInfo(isSalesforce || isThirdPartyAgent))
    localStorage.removeItem('B2BBookingFromSupportDashboard')
    resetAcceptedMedicaidSession()
  }

  return (
    <>
      {variant === 'button' ? (
        <CLButton
          theme="v2"
          variant="secondary"
          className={classNames(
            styles.new_search_booking_btn_v3,
            ctaClassOverride
          )}
          onClick={() => setShowModal(true)}
        >
          {displayText}
        </CLButton>
      ) : (
        <CLButton
          theme="v2"
          variant="link"
          className={classNames(ctaClassOverride)}
          onClick={() => setShowModal(true)}
        >
          {displayText}
        </CLButton>
      )}

      <ResetBookingModal
        show={showModal}
        onSubmit={resetBooking}
        onHide={() => setShowModal(false)}
      />
    </>
  )
}

export default ResetSupportBooking
